/**
 * CanvasLabelDesigner — WYSIWYG drag-and-drop label editor. Admins place
 * each field on a 60×50mm canvas, set font size + bold, add free static
 * text, and (per type) a barcode/QR. The backend renders TSPL at these
 * exact positions so the printed label matches the design.
 *
 * Extras: named layout presets per type (save / load / update / delete),
 * smart snap-and-align guides while dragging, and one-tap element
 * duplication.
 */
import { useEffect, useRef, useState } from "react";
import api, { withSite, formatErr } from "@/lib/api";
import {
  PX_PER_MM, CANVAS_TYPES, CANVAS_FIELDS, CANVAS_SPECIALS,
  FIELD_LABEL, defaultCanvasLayout, elementValue,
  FONT_OPTIONS, PT_OPTIONS, elPt, elFont, ROTATIONS,
  elRot, elAlign, elInvert, elTextStyle,
} from "@/lib/labelCanvas";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Type, Trash2, Save, RotateCcw, Bold, Copy, CopyPlus, BookmarkPlus, AlertTriangle, CheckCircle2, MoveHorizontal, AlignLeft, AlignCenter, AlignRight, Contrast } from "lucide-react";
import { LayoutThumb } from "@/components/workflow/LayoutThumb";
import { declarationName } from "@/lib/allergens";

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const SNAP_MM = 2; // snap threshold in mm
const uid = (p) => `${p}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
const deep = (o) => JSON.parse(JSON.stringify(o));

const _dt = (d) => d.toLocaleString("en-AU", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false });
const _date = (d) => d.toLocaleDateString("en-AU", { day: "2-digit", month: "2-digit", year: "numeric" });

// Build a real-data preview map for the canvas from a chosen product.
function buildPreviewData(p, type, site) {
  if (!p) return null;
  const now = new Date();
  const base = {
    site_name: site?.name || "",
    product_name: p.name,
    allergens: (p.allergens || []).map((a) => String(a).toUpperCase()).join(", "),
    storage: (p.storage || "").replace(/_/g, " ").toUpperCase(),
  };
  const decl = [];
  const al = (p.allergens || []).map(declarationName);
  const mc = (p.may_contain || []).map(declarationName);
  if (al.length) decl.push("Contains: " + al.join(", ") + ".");
  if (mc.length) decl.push("May be present: " + mc.join(", ") + ".");
  base.allergen_declaration = decl.join("\n");
  if (type === "prep") {
    const ub = new Date(now); ub.setHours(ub.getHours() + Number(p.shelf_life_hours || 48));
    return { ...base, prep_date: _dt(now), use_by_date: _dt(ub), staff_id: "STAFF" };
  }
  if (type === "retail") {
    const bb = new Date(now); bb.setDate(bb.getDate() + Number(p.best_before_days || 0));
    return {
      ...base,
      price: p.price_aud != null && p.price_aud !== "" ? `$${Number(p.price_aud).toFixed(2)}` : "",
      best_before: _date(bb),
      ingredients: p.ingredients || "",
      barcode: p.barcode || "",
    };
  }
  return base;
}

export default function CanvasLabelDesigner({ activeSite, refresh }) {
  const [type, setType] = useState("prep");
  const [layouts, setLayouts] = useState({});
  const [presets, setPresets] = useState({});          // { prep:[{id,name,layout}], ... }
  const [activePresetId, setActivePresetId] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [page, setPage] = useState(0);   // which physical label is being edited
  const [saving, setSaving] = useState(false);
  const [guides, setGuides] = useState({ x: null, y: null });
  const [nameDialog, setNameDialog] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [products, setProducts] = useState([]);
  const [previewProductId, setPreviewProductId] = useState("");
  const [estPages, setEstPages] = useState(1);
  const [estPerPage, setEstPerPage] = useState({});
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const resizeRef = useRef(null);
  const lastSiteId = useRef(null);

  useEffect(() => {
    // Only (re)load when the site actually changes. `refresh()` after a
    // preset save re-provides a new activeSite object with the SAME id —
    // reloading then would clobber activePresetId and wipe unsaved canvas
    // edits, so we skip those churn re-renders.
    if (!activeSite || lastSiteId.current === activeSite.id) return;
    lastSiteId.current = activeSite.id;
    const stored = activeSite.canvas_layouts || {};
    const next = {};
    CANVAS_TYPES.forEach(({ v }) => {
      // Normalise older/partial saved layouts so width/height always exist
      // (a missing width_mm would make the drag clamp NaN).
      next[v] = stored[v] && stored[v].elements
        ? { width_mm: 60, height_mm: 50, enabled: false, ...stored[v] }
        : defaultCanvasLayout(v);
    });
    setLayouts(next);
    setPresets(activeSite.canvas_layout_presets || {});
    setSelectedId(null);
    setActivePresetId("");
  }, [activeSite]);

  const layout = layouts[type] || defaultCanvasLayout(type);
  const W = (layout.width_mm || 60) * PX_PER_MM;
  const H = (layout.height_mm || 50) * PX_PER_MM;
  const selected = (layout.elements || []).find((e) => e.id === selectedId) || null;
  // Pagination: how many physical labels this design spans.
  const pageCount = (layout.elements || []).reduce((m, e) => Math.max(m, (e.page || 0) + 1), 1);
  const pageTabs = Math.max(pageCount, page + 1);
  const curPage = Math.min(page, pageTabs - 1);

  const removePage = () => {
    // Drop every element on the current label and shift higher labels down.
    setLayout((l) => ({
      ...l,
      elements: (l.elements || [])
        .filter((e) => (e.page || 0) !== curPage)
        .map((e) => ((e.page || 0) > curPage ? { ...e, page: (e.page || 0) - 1 } : e)),
    }));
    setPage((p) => Math.max(0, p - 1));
    setSelectedId(null);
  };
  const typePresets = presets[type] || [];

  // Fetch products for the real-data preview (prep / retail only).
  useEffect(() => {
    setPreviewProductId("");
    if (!activeSite || (type !== "prep" && type !== "retail")) { setProducts([]); return; }
    api.get("/products", withSite(activeSite.id, { category: type }))
      .then((r) => setProducts(r.data || []))
      .catch(() => setProducts([]));
  }, [activeSite, type]);

  const previewProduct = products.find((p) => p.id === previewProductId) || null;
  const previewData = buildPreviewData(previewProduct, type, activeSite);

  // Live "how many labels will this print?" estimate (debounced) so admins
  // see a warning before staff hit print.
  useEffect(() => {
    if (!activeSite) return undefined;
    const handle = setTimeout(async () => {
      const p = previewProduct;
      const data = p
        ? {
            product_name: p.name,
            allergens: p.allergens || [],
            price_aud: p.price_aud,
            ingredients: p.ingredients || "",
            barcode: p.barcode || "",
            storage: p.storage || "",
            prep_date: "01/07/26 08:00",
            use_by_date: "03/07/26 08:00",
            staff_id: "STAFF",
            best_before: "10/07/2026",
          }
        : {
            product_name: "Sample Product Name",
            allergens: ["milk", "egg"],
            price_aud: 9.99,
            ingredients: "Water, wheat flour, sugar, salt, yeast",
            barcode: "9312345678907",
            storage: "refrigerated",
            prep_date: "01/07/26 08:00",
            use_by_date: "03/07/26 08:00",
            staff_id: "STAFF",
            best_before: "10/07/2026",
          };
      try {
        const { data: res } = await api.post("/labels/estimate-pages", { layout, data }, withSite(activeSite.id));
        setEstPages(res.pages || 1);
        setEstPerPage(res.per_page || {});
      } catch {
        setEstPages(1);
        setEstPerPage({});
      }
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout, previewProductId, activeSite]);

  const setLayout = (updater) =>
    setLayouts((prev) => ({ ...prev, [type]: updater(prev[type] || defaultCanvasLayout(type)) }));

  const updateEl = (id, patch) =>
    setLayout((l) => ({ ...l, elements: l.elements.map((e) => (e.id === id ? { ...e, ...patch } : e)) }));

  const addField = (key) =>
    setLayout((l) => ({
      ...l,
      elements: [...l.elements, { id: uid(key), type: "field", key, x: 3, y: 3, size: 3, bold: false, visible: true, page }],
    }));

  const addText = () => {
    const text = window.prompt("Static text to print:", "KEEP REFRIGERATED");
    if (!text) return;
    setLayout((l) => ({
      ...l,
      elements: [...l.elements, { id: uid("text"), type: "text", text, x: 3, y: 3, size: 2, bold: false, visible: true, page }],
    }));
  };

  const addSpecial = (sp) =>
    setLayout((l) => ({
      ...l,
      elements: [...l.elements, sp.type === "qr"
        ? { id: uid("qr"), type: "qr", x: 40, y: 28, cell: 4, visible: true, page, ...(sp.source ? { source: sp.source } : {}) }
        : { id: uid("barcode"), type: "barcode", x: 3, y: 40, height: 8, visible: true, page }],
    }));

  const deleteEl = (id) => {
    setLayout((l) => ({ ...l, elements: l.elements.filter((e) => e.id !== id) }));
    setSelectedId(null);
  };

  const duplicateEl = () => {
    if (!selected) return;
    const wmm = layout.width_mm || 60;
    const hmm = layout.height_mm || 50;
    const copy = {
      ...deep(selected),
      id: uid(selected.type),
      x: clamp((selected.x || 0) + 2, 0, wmm - 1),
      y: clamp((selected.y || 0) + 2, 0, hmm - 1),
    };
    setLayout((l) => ({ ...l, elements: [...l.elements, copy] }));
    setSelectedId(copy.id);
  };

  // Copy the selected element onto the NEXT physical label — handy for
  // shared headers/logos that should repeat across a multi-label design.
  const copyToNextPage = () => {
    if (!selected) return;
    const target = curPage + 1;
    const copy = { ...deep(selected), id: uid(selected.type), page: target };
    setLayout((l) => ({ ...l, elements: [...l.elements, copy] }));
    setPage(target);
    setSelectedId(copy.id);
    toast.success(`Copied to Label ${target + 1}`);
  };

  // ---- dragging (pointer events) + snap/align guides ----
  const onPointerDown = (e, el) => {
    e.stopPropagation();
    setSelectedId(el.id);
    const rect = canvasRef.current.getBoundingClientRect();
    dragRef.current = {
      id: el.id,
      offX: e.clientX - rect.left - (el.x || 0) * PX_PER_MM,
      offY: e.clientY - rect.top - (el.y || 0) * PX_PER_MM,
    };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  };
  const onResizeDown = (e, el) => {
    e.stopPropagation();
    setSelectedId(el.id);
    resizeRef.current = { id: el.id };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  };
  const onPointerMove = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const wmm = layout.width_mm || 60;
    const hmm = layout.height_mm || 50;
    if (resizeRef.current) {
      const el = layout.elements.find((x) => x.id === resizeRef.current.id);
      if (!el) return;
      const boxW = clamp((e.clientX - rect.left) / PX_PER_MM - (el.x || 0), 4, wmm - (el.x || 0));
      updateEl(resizeRef.current.id, { w: Math.round(boxW) });
      return;
    }
    if (!dragRef.current) return;
    let xmm = clamp((e.clientX - rect.left - dragRef.current.offX) / PX_PER_MM, 0, wmm - 1);
    let ymm = clamp((e.clientY - rect.top - dragRef.current.offY) / PX_PER_MM, 0, hmm - 1);

    // Snap to other elements' edges + the canvas centre/left margin.
    const others = layout.elements.filter((el) => el.id !== dragRef.current.id);
    const xTargets = [0, Math.round(wmm / 2), ...others.map((el) => el.x || 0)];
    const yTargets = [0, Math.round(hmm / 2), ...others.map((el) => el.y || 0)];
    let gx = null;
    let gy = null;
    for (const t of xTargets) { if (Math.abs(xmm - t) <= SNAP_MM) { xmm = t; gx = t; break; } }
    for (const t of yTargets) { if (Math.abs(ymm - t) <= SNAP_MM) { ymm = t; gy = t; break; } }
    setGuides({ x: gx, y: gy });
    updateEl(dragRef.current.id, { x: Math.round(xmm), y: Math.round(ymm) });
  };
  const onPointerUp = () => { dragRef.current = null; resizeRef.current = null; setGuides({ x: null, y: null }); };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/sites/${activeSite.id}`, { canvas_layouts: layouts }, withSite(activeSite.id));
      if (refresh) await refresh();
      toast.success("Canvas layouts saved");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const resetType = () => {
    setLayout(() => defaultCanvasLayout(type));
    setSelectedId(null);
    setActivePresetId("");
  };

  // ---- named presets ----
  const persistPresets = async (nextPresets) => {
    setPresets(nextPresets);
    try {
      await api.patch(`/sites/${activeSite.id}`, { canvas_layout_presets: nextPresets }, withSite(activeSite.id));
      if (refresh) await refresh();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const loadPreset = (id) => {
    const p = typePresets.find((x) => x.id === id);
    if (!p) return;
    setLayout((l) => ({ ...deep(p.layout), enabled: l.enabled }));
    setActivePresetId(id);
    setSelectedId(null);
    toast.success(`Loaded "${p.name}"`);
  };

  const confirmSaveAs = async () => {
    const name = presetName.trim();
    if (!name) { toast.error("Name the layout first"); return; }
    const entry = { id: uid("preset"), name, layout: deep(layout) };
    const next = { ...presets, [type]: [...typePresets, entry] };
    setNameDialog(false);
    setPresetName("");
    setActivePresetId(entry.id);
    await persistPresets(next);
    toast.success(`Saved layout "${name}"`);
  };

  const updatePreset = async () => {
    if (!activePresetId) return;
    const next = {
      ...presets,
      [type]: typePresets.map((p) => (p.id === activePresetId ? { ...p, layout: deep(layout) } : p)),
    };
    await persistPresets(next);
    const p = typePresets.find((x) => x.id === activePresetId);
    toast.success(`Updated "${p?.name || "layout"}"`);
  };

  const deletePreset = async () => {
    if (!activePresetId) return;
    const p = typePresets.find((x) => x.id === activePresetId);
    if (!window.confirm(`Delete layout "${p?.name}"?`)) return;
    const next = { ...presets, [type]: typePresets.filter((x) => x.id !== activePresetId) };
    setActivePresetId("");
    await persistPresets(next);
    toast.success("Layout deleted");
  };

  // Branch the current working design into a brand-new named preset.
  const duplicateLayout = async () => {
    const src = typePresets.find((x) => x.id === activePresetId);
    const entry = { id: uid("preset"), name: `${src?.name || "Layout"} copy`, layout: deep(layout) };
    const next = { ...presets, [type]: [...typePresets, entry] };
    setActivePresetId(entry.id);
    await persistPresets(next);
    toast.success(`Duplicated as "${entry.name}"`);
  };

  const placedKeys = new Set(layout.elements.filter((e) => e.type === "field").map((e) => e.key));
  const availableFields = (CANVAS_FIELDS[type] || []).filter((k) => !placedKeys.has(k));

  return (
    <div className="mt-4 space-y-4" data-testid="canvas-designer">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex gap-1 p-1 rounded-lg bg-eucalyptus-100">
          {CANVAS_TYPES.map(({ v, label }) => (
            <button
              key={v}
              data-testid={`canvas-type-${v}`}
              onClick={() => { setType(v); setSelectedId(null); setActivePresetId(""); }}
              className={`px-3 py-1.5 rounded-md text-sm font-semibold ${type === v ? "bg-eucalyptus-900 text-white" : "text-eucalyptus-900 hover:bg-eucalyptus-200"}`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Label htmlFor="canvas-enabled" className="text-sm">Use canvas for {type}</Label>
            <Switch
              id="canvas-enabled"
              data-testid="canvas-enabled"
              checked={!!layout.enabled}
              onCheckedChange={(c) => setLayout((l) => ({ ...l, enabled: c }))}
            />
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="canvas-autoshrink" className="text-sm" title="Try shrinking text to fit one label before spilling onto a 2nd">Fit on one label</Label>
            <Switch
              id="canvas-autoshrink"
              data-testid="canvas-autoshrink"
              checked={layout.auto_shrink !== false}
              onCheckedChange={(c) => setLayout((l) => ({ ...l, auto_shrink: c }))}
            />
          </div>
        </div>
      </div>

      {/* Named layout library */}
      <div className="surface p-4 flex flex-wrap items-end gap-3" data-testid="canvas-presets">
        <div className="min-w-[220px]">
          <Label className="text-xs">Saved layouts ({type})</Label>
          <Select value={activePresetId || undefined} onValueChange={loadPreset}>
            <SelectTrigger data-testid="canvas-preset-select" className="h-9">
              <SelectValue placeholder={typePresets.length ? "Load a saved layout…" : "No saved layouts yet"} />
            </SelectTrigger>
            <SelectContent>
              {typePresets.map((p) => (
                <SelectItem key={p.id} value={p.id} data-testid={`canvas-preset-opt-${p.id}`}>
                  <span className="flex items-center gap-2">
                    <LayoutThumb layout={p.layout} />
                    {p.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="canvas-preset-save-as" size="sm" variant="outline" className="gap-1 h-9" onClick={() => { setPresetName(""); setNameDialog(true); }}>
          <BookmarkPlus className="w-4 h-4" /> Save as…
        </Button>
        <Button data-testid="canvas-preset-duplicate" size="sm" variant="outline" className="gap-1 h-9" onClick={duplicateLayout}>
          <Copy className="w-4 h-4" /> Duplicate this layout
        </Button>
        {activePresetId && (
          <>
            <Button data-testid="canvas-preset-update" size="sm" variant="outline" className="gap-1 h-9" onClick={updatePreset}>
              <Save className="w-4 h-4" /> Update
            </Button>
            <Button data-testid="canvas-preset-delete" size="sm" variant="outline" className="gap-1 h-9 text-red-700 border-red-200" onClick={deletePreset}>
              <Trash2 className="w-4 h-4" /> Delete
            </Button>
          </>
        )}
      </div>

      <div className="grid lg:grid-cols-[auto_1fr] gap-6">
        {/* Canvas */}
        <div>
          {(type === "prep" || type === "retail") && (
            <div className="mb-2 flex items-center gap-2" data-testid="canvas-preview-picker">
              <Label className="text-xs whitespace-nowrap text-charcoal-soft">Preview with</Label>
              <Select value={previewProductId || undefined} onValueChange={setPreviewProductId}>
                <SelectTrigger data-testid="canvas-preview-product" className="h-8 text-xs w-56"><SelectValue placeholder="Sample data" /></SelectTrigger>
                <SelectContent>
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`canvas-preview-opt-${p.id}`}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {previewProductId && (
                <button data-testid="canvas-preview-clear" onClick={() => setPreviewProductId("")} className="text-xs text-eucalyptus-800 underline">use sample</button>
              )}
            </div>
          )}
          {/* Multi-label pages — design each physical label; long text /
              off-edge elements auto-flow onto the next label at print. */}
          <div className="flex items-center gap-1.5 mb-2 flex-wrap" data-testid="canvas-pages">
            {Array.from({ length: pageTabs }).map((_, i) => (
              <button
                key={i}
                data-testid={`canvas-page-${i}`}
                onClick={() => { setPage(i); setSelectedId(null); }}
                className={`text-xs px-2.5 h-7 rounded-full border transition-colors ${i === curPage ? "bg-eucalyptus-900 text-white border-eucalyptus-900" : "bg-white text-charcoal-soft border-[color:var(--border)] hover:border-eucalyptus-700"}`}
              >
                Label {i + 1}
              </button>
            ))}
            <button data-testid="canvas-add-page" onClick={() => { setPage(pageTabs); setSelectedId(null); }} className="text-xs px-2.5 h-7 rounded-full border border-dashed border-ochre-500 text-ochre-700 hover:bg-ochre-50">
              + Add label
            </button>
            {pageTabs > 1 && (
              <button data-testid="canvas-remove-page" onClick={removePage} className="text-xs px-2 h-7 rounded-full text-red-600 hover:bg-red-50" title="Delete this label page">
                Remove
              </button>
            )}
          </div>
          {/* Per-page fit hint — does the CURRENT label hold its content, or
              does it spill onto continuation labels at print time? */}
          {(() => {
            const spill = Math.max(1, Number(estPerPage[String(curPage)] || 1));
            const full = spill > 1;
            return (
              <div
                data-testid="canvas-page-fit-note"
                className={`mb-2 inline-flex items-center gap-1.5 text-xs font-semibold rounded-md px-2.5 py-1 ${
                  full ? "bg-amber-100 text-amber-900" : "bg-eucalyptus-100 text-eucalyptus-900"
                }`}
              >
                {full ? (
                  <><AlertTriangle className="w-3.5 h-3.5" /> Label {curPage + 1} is full — content flows onto {spill} labels</>
                ) : (
                  <><CheckCircle2 className="w-3.5 h-3.5" /> Label {curPage + 1} fits on one label</>
                )}
              </div>
            );
          })()}
          <div
            ref={canvasRef}
            data-testid="canvas-surface"
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            onPointerDown={() => setSelectedId(null)}
            className="relative bg-white border-2 border-dashed border-eucalyptus-700/50 shadow-inner select-none"
            style={{ width: W, height: H, touchAction: "none", backgroundSize: `${PX_PER_MM * 5}px ${PX_PER_MM * 5}px`, backgroundImage: "linear-gradient(to right,#0001 1px,transparent 1px),linear-gradient(to bottom,#0001 1px,transparent 1px)" }}
          >
            {/* Alignment guides */}
            {guides.x != null && (
              <div data-testid="canvas-guide-x" className="absolute top-0 bottom-0 pointer-events-none" style={{ left: guides.x * PX_PER_MM, width: 1, background: "var(--ochre-600, #b45309)" }} />
            )}
            {guides.y != null && (
              <div data-testid="canvas-guide-y" className="absolute left-0 right-0 pointer-events-none" style={{ top: guides.y * PX_PER_MM, height: 1, background: "var(--ochre-600, #b45309)" }} />
            )}
            {layout.elements.filter((el) => (el.page || 0) === curPage).map((el) => {
              const isText = el.type === "field" || el.type === "text";
              const posStyle = {
                left: (el.x || 0) * PX_PER_MM,
                top: (el.y || 0) * PX_PER_MM,
                touchAction: "none",
              };
              const style = isText
                ? { ...posStyle, ...elTextStyle(el, W) }
                : {
                    ...posStyle,
                    width: undefined,
                    maxWidth: undefined,
                    whiteSpace: "nowrap",
                    wordBreak: "break-word",
                    fontSize: 10,
                    fontWeight: el.bold ? 800 : 400,
                    fontFamily: el.type === "barcode" ? "monospace" : "inherit",
                  };
              return (
              <div
                key={el.id}
                data-testid={`canvas-el-${el.id}`}
                onPointerDown={(e) => onPointerDown(e, el)}
                className={`absolute cursor-move leading-tight px-0.5 ${selectedId === el.id ? "outline outline-2 outline-ochre-500" : ""} ${el.visible === false ? "opacity-30" : ""}`}
                style={style}
              >
                {el.type === "qr"
                  ? <div className="border border-black grid place-items-center font-bold text-center leading-none" style={{ width: (el.cell || 4) * 8, height: (el.cell || 4) * 8, fontSize: el.source === "trust" ? 6 : 9 }}>{el.source === "trust" ? "TRUST QR" : "QR"}</div>
                  : elementValue(el, previewData)}
                {selectedId === el.id && el.type !== "qr" && el.type !== "barcode" && (
                  <div
                    data-testid={`canvas-resize-${el.id}`}
                    onPointerDown={(e) => onResizeDown(e, el)}
                    title="Drag to set wrap width"
                    className="absolute top-0 -right-1.5 w-3 h-full bg-ochre-500/80 hover:bg-ochre-500 cursor-ew-resize rounded-sm"
                    style={{ touchAction: "none" }}
                  />
                )}
              </div>
              );
            })}
          </div>
          <p className="text-xs text-charcoal-soft mt-2">60mm × 50mm · drag elements to position · grid = 5mm · edges snap &amp; align</p>
        </div>

        {/* Right panel: palette + inspector */}
        <div className="space-y-4">
          <div className="surface p-4 space-y-2">
            <div className="text-sm font-semibold">Add element</div>
            <div className="flex flex-wrap gap-2">
              {availableFields.map((k) => (
                <Button key={k} data-testid={`canvas-add-${k}`} size="sm" variant="outline" className="gap-1" onClick={() => addField(k)}>
                  <Plus className="w-3 h-3" /> {FIELD_LABEL[k] || k}
                </Button>
              ))}
              <Button data-testid="canvas-add-text" size="sm" variant="outline" className="gap-1" onClick={addText}>
                <Type className="w-3 h-3" /> Text
              </Button>
              {(CANVAS_SPECIALS[type] || []).map((sp) => (
                <Button key={sp.source ? `${sp.type}-${sp.source}` : sp.type} data-testid={`canvas-add-${sp.source || sp.type}`} size="sm" variant="outline" className="gap-1" onClick={() => addSpecial(sp)}>
                  <Plus className="w-3 h-3" /> {sp.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="surface p-4 space-y-3" data-testid="canvas-inspector">
            <div className="text-sm font-semibold">
              {selected ? `Editing: ${selected.type === "text" ? "Text" : selected.type === "field" ? (FIELD_LABEL[selected.key] || selected.key) : selected.type}` : "Select an element"}
            </div>
            {selected && (
              <>
                {(selected.type === "field" || selected.type === "text") && (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs">Font</Label>
                      <Select value={elFont(selected)} onValueChange={(v) => updateEl(selected.id, { font: v })}>
                        <SelectTrigger data-testid="canvas-font" className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {FONT_OPTIONS.map((f) => (
                            <SelectItem key={f.v} value={f.v} data-testid={`canvas-font-opt-${f.v}`}>
                              <span style={{ fontFamily: f.css, fontStretch: f.stretch || "normal", letterSpacing: f.tracking || "normal" }}>{f.label}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end gap-3">
                      <div className="flex-1">
                        <Label className="text-xs">Point size</Label>
                        <Select value={String(elPt(selected))} onValueChange={(v) => updateEl(selected.id, { pt: Number(v) })}>
                          <SelectTrigger data-testid="canvas-pt" className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent className="max-h-64">
                            {PT_OPTIONS.map((pt) => <SelectItem key={pt} value={String(pt)} data-testid={`canvas-pt-opt-${pt}`}>{pt} pt</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        data-testid="canvas-bold"
                        size="sm"
                        variant={selected.bold ? "default" : "outline"}
                        className={selected.bold ? "bg-eucalyptus-900 text-white" : ""}
                        onClick={() => updateEl(selected.id, { bold: !selected.bold })}
                      >
                        <Bold className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Alignment */}
                    <div>
                      <Label className="text-xs">Alignment</Label>
                      <div className="inline-flex gap-1 rounded-md border p-1" style={{ borderColor: "var(--border)" }} data-testid="canvas-align">
                        {[
                          { v: "left", Icon: AlignLeft },
                          { v: "center", Icon: AlignCenter },
                          { v: "right", Icon: AlignRight },
                        ].map(({ v, Icon }) => (
                          <button
                            key={v}
                            data-testid={`canvas-align-${v}`}
                            onClick={() => updateEl(selected.id, { align: v })}
                            className={`w-8 h-8 grid place-items-center rounded ${elAlign(selected) === v ? "bg-eucalyptus-900 text-white" : "text-eucalyptus-900 hover:bg-eucalyptus-100"}`}
                            aria-label={`Align ${v}`}
                          >
                            <Icon className="w-4 h-4" />
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Rotation + Invert */}
                    <div className="flex items-end gap-4 flex-wrap">
                      <div>
                        <Label className="text-xs">Rotate</Label>
                        <div className="inline-flex gap-1 rounded-md border p-1" style={{ borderColor: "var(--border)" }} data-testid="canvas-rotate">
                          {ROTATIONS.map((r) => (
                            <button
                              key={r}
                              data-testid={`canvas-rotate-${r}`}
                              onClick={() => updateEl(selected.id, { rot: r })}
                              className={`px-2 h-8 rounded text-xs font-mono ${elRot(selected) === r ? "bg-eucalyptus-900 text-white" : "text-eucalyptus-900 hover:bg-eucalyptus-100"}`}
                            >
                              {r}°
                            </button>
                          ))}
                        </div>
                      </div>
                      <Button
                        data-testid="canvas-invert"
                        size="sm"
                        variant={elInvert(selected) ? "default" : "outline"}
                        className={`gap-1 ${elInvert(selected) ? "bg-eucalyptus-900 text-white" : ""}`}
                        onClick={() => updateEl(selected.id, { invert: !elInvert(selected) })}
                        title="White text on a black background"
                      >
                        <Contrast className="w-4 h-4" /> Invert
                      </Button>
                    </div>
                  </div>
                )}
                {selected.type === "barcode" && (
                  <div>
                    <Label className="text-xs">Barcode height (mm)</Label>
                    <Select value={String(selected.height || 8)} onValueChange={(v) => updateEl(selected.id, { height: Number(v) })}>
                      <SelectTrigger data-testid="canvas-barcode-height" className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>{[6, 8, 10, 12, 15].map((h) => <SelectItem key={h} value={String(h)}>{h}mm</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                {selected.type === "qr" && (
                  <div>
                    <Label className="text-xs">QR size</Label>
                    <Select value={String(selected.cell || 4)} onValueChange={(v) => updateEl(selected.id, { cell: Number(v) })}>
                      <SelectTrigger data-testid="canvas-qr-cell" className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>{[3, 4, 5, 6, 8].map((c) => <SelectItem key={c} value={String(c)}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div className="text-xs text-charcoal-soft">Position: {selected.x || 0}mm, {selected.y || 0}mm</div>
                {(selected.type === "field" || selected.type === "text") && (
                  <div className="flex items-center gap-2 text-xs text-charcoal-soft" data-testid="canvas-wrap-width">
                    <MoveHorizontal className="w-3.5 h-3.5" />
                    Wrap width: {selected.w ? `${selected.w}mm` : "auto (to edge)"}
                    {selected.w && (
                      <button
                        data-testid="canvas-wrap-auto"
                        className="text-eucalyptus-800 underline"
                        onClick={() => updateEl(selected.id, { w: undefined })}
                      >
                        auto
                      </button>
                    )}
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button data-testid="canvas-duplicate" size="sm" variant="outline" className="gap-1" onClick={duplicateEl}>
                    <Copy className="w-4 h-4" /> Duplicate
                  </Button>
                  <Button data-testid="canvas-copy-next-page" size="sm" variant="outline" className="gap-1" onClick={copyToNextPage} title={`Copy this element onto Label ${curPage + 2}`}>
                    <CopyPlus className="w-4 h-4" /> Copy to next label
                  </Button>
                  <Button data-testid="canvas-delete" size="sm" variant="outline" className="gap-1 text-red-700 border-red-200" onClick={() => deleteEl(selected.id)}>
                    <Trash2 className="w-4 h-4" /> Remove
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap justify-end items-center gap-3">
        <span
          data-testid="canvas-fit-note"
          className={`mr-auto inline-flex items-center gap-1.5 text-sm font-semibold rounded-full px-3 py-1.5 ${
            estPages > 1 ? "bg-amber-100 text-amber-900" : "bg-eucalyptus-100 text-eucalyptus-900"
          }`}
          title={previewProduct ? `Estimated for ${previewProduct.name}` : "Estimated with sample data — pick a product above for an exact count"}
        >
          {estPages > 1 ? (
            <><AlertTriangle className="w-4 h-4" /> This design needs {estPages} labels per print</>
          ) : (
            <><CheckCircle2 className="w-4 h-4" /> Fits on 1 label</>
          )}
        </span>
        <Button data-testid="canvas-reset" variant="ghost" className="gap-2" onClick={resetType}>
          <RotateCcw className="w-4 h-4" /> Reset {type}
        </Button>
        <Button data-testid="canvas-save" disabled={saving} className="bg-ochre-600 hover:bg-ochre-400 text-white gap-2" onClick={save}>
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save canvas layouts"}
        </Button>
      </div>

      {/* Name-a-layout dialog */}
      <Dialog open={nameDialog} onOpenChange={setNameDialog}>
        <DialogContent data-testid="canvas-preset-dialog">
          <DialogHeader>
            <DialogTitle>Save layout as…</DialogTitle>
            <DialogDescription>Give this {type} design a name so you can reuse it for other products.</DialogDescription>
          </DialogHeader>
          <Input
            data-testid="canvas-preset-name"
            autoFocus
            placeholder="e.g. Freezer stickers, Front-of-house"
            value={presetName}
            onChange={(e) => setPresetName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") confirmSaveAs(); }}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNameDialog(false)}>Cancel</Button>
            <Button data-testid="canvas-preset-confirm" className="bg-ochre-600 hover:bg-ochre-400 text-white" onClick={confirmSaveAs}>Save layout</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
